import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { getJiraProjectsUsers } from '../../../../modules/jira';
import useDebounce from '../../../../hooks/useDebounce';

const JiraUserSelector = ({ value, onChange, projectId, jiraProjectId }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchQuery = useDebounce(searchValue, 500);

  const selectedUser = useMemo(() => {
    return users.find((user) => user.account_id === value) || null;
  }, [users, value]);

  const fetchUsers = useCallback(
    async (searchTerm = '') => {
      if (!jiraProjectId || !projectId) return;
      setLoading(true);
      try {
        const response = await getJiraProjectsUsers(
          jiraProjectId,
          projectId,
          searchTerm
        );
        setUsers(response?.values || []);
      } catch (error) {
        setUsers([]);
        console.error('Error fetching users:', error);
      } finally {
        setLoading(false);
      }
    },
    [jiraProjectId, projectId]
  );

  useEffect(() => {
    if (debouncedSearchQuery.length >= 2 || debouncedSearchQuery === '') {
      fetchUsers(debouncedSearchQuery);
    }
  }, [debouncedSearchQuery, fetchUsers]);

  const handleInputChange = (_, newInputValue) => {
    setSearchValue(newInputValue);
  };

  const handleChange = (_, newValue) => {
    if (newValue?.account_id !== value) {
      onChange(newValue?.account_id || null);
    }
  };

  return (
    <Autocomplete
      options={users}
      getOptionLabel={(option) => option.display_name || ''}
      value={selectedUser}
      onChange={handleChange}
      inputValue={searchValue}
      onInputChange={handleInputChange}
      loading={loading}
      loadingText="Loading..."
      noOptionsText={!loading && users.length === 0 ? 'No users found' : ''}
      filterSelectedOptions
      renderInput={(params) => (
        <TextField {...params} variant="outlined" fullWidth />
      )}
    />
  );
};

export default JiraUserSelector;
